'use client';

import { ClipboardCopy, Download } from "lucide-react";
import { useState } from "react";
import './GCodePanel.scss';

interface GCodePanelProps {
  gcode: string;
  steps?: any[];
  controlValues?: Record<string, any>;
  title?: string;
}

type TabType = 'gcode' | 'steps';

export default function GCodePanel({
  gcode,
  steps = [],
  controlValues,
  title = "sketch"
}: GCodePanelProps) {
  const [activeTab, setActiveTab] = useState<TabType>('gcode');

  // Helper to get sketch URL (editor or view)
  const getSketchUrl = () => {
    if (typeof window !== 'undefined') {
      const baseUrl = window.location.origin + window.location.pathname;
      // Prefer controlValues if provided
      const params = new URLSearchParams();
      if (controlValues && Object.keys(controlValues).length > 0) {
        Object.entries(controlValues).forEach(([key, value]) => {
          params.set(key, String(value));
        });
      } else if (steps && steps.length > 0) {
        Object.keys(steps[0]).forEach(key => {
          if (!key.startsWith('_') && key !== '__class__' && key !== 'type' && key !== 'constructor') {
            params.set(key, String(steps[0][key]));
          }
        });
      }
      const query = params.toString();
      return query ? `${baseUrl}?${query}` : baseUrl;
    }
    return '';
  };

  // Helper to extract parameters from steps (if any)
  const getParameters = () => {
    // Prefer controlValues if provided
    if (controlValues && Object.keys(controlValues).length > 0) {
      return Object.entries(controlValues).map(([key, value]) => `${key}: ${JSON.stringify(value)}`);
    }
    if (!steps || steps.length === 0) return null;
    // Collect all unique keys from all steps
    const paramSet = new Set<string>();
    steps.forEach(step => {
      Object.keys(step).forEach(key => {
        if (!key.startsWith('_') && key !== '__class__' && key !== 'type' && key !== 'constructor') {
          paramSet.add(key);
        }
      });
    });
    // Build param: value pairs from first step (or all steps if needed)
    const params: string[] = [];
    paramSet.forEach(key => {
      const value = steps[0][key];
      params.push(`${key}: ${JSON.stringify(value)}`);
    });
    return params.length > 0 ? params : null;
  };

  // Format G-code with comments
  const formatGcodeWithComments = () => {
    let commentLines: string[] = [];
    const sketchUrl = getSketchUrl();
    if (sketchUrl) commentLines.push(`; Source: "${title}" ${sketchUrl}`);
    const params = getParameters();
    if (params) {
      commentLines.push('; Parameters:');
      params.forEach(param => commentLines.push(`;   ${param}`));
    }
    return commentLines.length > 0 ? `${commentLines.join('\n')}\n${gcode}` : gcode;
  };

  const handleDownload = () => {
    const formattedGcode = formatGcodeWithComments();
    const blob = new Blob([formattedGcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}.gcode`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Format steps data for display as a readable list
  const formatStepsData = () => {
    if (!steps || steps.length === 0) {
      return 'No steps data available';
    }

    return steps.map((step, index) => {
      // Try multiple ways to get the type name
      let typeName = 'Step';

      // Check for Python-style class name
      if (step.__class__) {
        typeName = step.__class__;
      }
      // Check for type property (common in FullControl)
      else if (step.type) {
        typeName = step.type;
      }
      // Try to infer from properties
      else if ('x' in step && 'y' in step && 'z' in step) {
        typeName = 'Point';
      } else if ('on' in step && typeof step.on === 'boolean') {
        typeName = 'Extruder';
      } else if ('width' in step || 'height' in step) {
        typeName = 'ExtrusionGeometry';
      }

      // Extract all enumerable properties
      const params: string[] = [];
      const keys = Object.keys(step);

      for (const key of keys) {
        // Skip internal/meta properties
        if (key.startsWith('_') || key === '__class__' || key === 'type' || key === 'constructor') {
          continue;
        }

        const value = step[key];

        // Format the value
        let formattedValue: string;
        if (typeof value === 'number') {
          // Round numbers to 3 decimal places
          formattedValue = Number.isInteger(value) ? value.toString() : value.toFixed(3);
        } else if (typeof value === 'boolean') {
          formattedValue = value.toString();
        } else if (typeof value === 'string') {
          formattedValue = `"${value}"`;
        } else if (value === null) {
          formattedValue = 'null';
        } else if (value === undefined) {
          continue; // Skip undefined
        } else if (typeof value === 'object' && value !== null) {
          // For nested objects, show a simplified representation
          try {
            formattedValue = JSON.stringify(value);
          } catch {
            formattedValue = '[Object]';
          }
        } else {
          formattedValue = String(value);
        }

        params.push(`${key}: ${formattedValue}`);
      }

      const paramsStr = params.length > 0 ? `{${params.join(', ')}}` : '';
      return `${index + 1}. ${typeName}(${paramsStr})`;
    }).join('\n');
  };

  const handleCopy = () => {
    const content = activeTab === 'gcode' ? gcode : formatStepsData();
    navigator.clipboard.writeText(content);
  };

  return (
    <div className="gcode-panel-container">
      <div className="panel-header">
        <div className="panel-title">
          <h3>
            Output
          </h3>
          <div className="tab-buttons">
            <button
              className={`tab-btn ${activeTab === 'gcode' ? 'active' : ''}`}
              onClick={() => setActiveTab('gcode')}
              title="View G-Code Output"
            >
              G-Code
            </button>
            <button
              className={`tab-btn ${activeTab === 'steps' ? 'active' : ''}`}
              onClick={() => setActiveTab('steps')}
              title="View Steps Object (Pre-Transform)"
            >
              Steps
            </button>
          </div>
          <button
            className="copy-btn"
            onClick={handleCopy}
            title={`Copy ${activeTab === 'gcode' ? 'G-Code' : 'Steps'} to Clipboard`}
          >
            <ClipboardCopy size={16} />
          </button>
          {activeTab === 'gcode' && (
            <button
              className="download-btn"
              onClick={handleDownload}
              title="Download G-Code as File"
              disabled={!gcode}
            >
              <Download size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="gcode-panel">
        {activeTab === 'gcode' ? (
          gcode ? (
            <pre className="gcode-output">{formatGcodeWithComments()}</pre>
          ) : (
            <div className="placeholder">
              Run the code to generate G-code
            </div>
          )
        ) : (
          steps && steps.length > 0 ? (
            <pre className="gcode-output steps-output">{formatStepsData()}</pre>
          ) : (
            <div className="placeholder">
              Run the code to generate steps data
            </div>
          )
        )}
      </div>
    </div>
  );
}
